import LineSidebar from "@/components/reactbits/LineSidebar";

/**
 * The three booking offerings the host can build, all live: "adhoc" is a fixed link with an expiry
 * and generated slots, "rolling link" a personalised always-on rolling link, and "poll" one event whose
 * time a group votes on.
 */
export type BookingOffering = "adhoc" | "rollingLink" | "poll";

// Sidebar row order ↔ offering key. The reactbits LineSidebar works in string
// labels + indices, so this array is the single source of truth for both.
const ORDER: BookingOffering[] = ["adhoc", "rollingLink", "poll"];
const LABELS = ["Ad-hoc appointment", "Rolling appointment", "Poll"];

/**
 * Offering switcher — the React Bits {@link LineSidebar} (cursor-proximity line
 * nav), themed with the app's tokens so it tracks light/dark. Purely a selector:
 * it owns no data, just maps a clicked row back to a {@link BookingOffering}.
 */
export function BookingOfferingSidebar({
  value,
  onChange,
  selected = true,
}: {
  value: BookingOffering;
  onChange: (next: BookingOffering) => void;
  /** False while another view owns the page, so no offering is marked as the current one. */
  selected?: boolean;
}) {
  // LineSidebar seeds its highlight from defaultActive once, so it's keyed to re-seed whenever
  // the selection changes underneath it (picking an offering, or leaving for "All bookings").
  const activeIndex = selected ? ORDER.indexOf(value) : null;

  return (
    <div className="shrink-0 lg:w-64">
      {/* A caption over the rows below, not somewhere you can go. Recessed so it reads as a group
          heading rather than as another destination beside "All bookings". */}
      <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Offerings
      </p>
      {/* overflow-hidden fences the line-nav's generous pointer hit-area so it
          can't reach into the builder column to the right. */}
      <div className="overflow-hidden">
        <LineSidebar
          key={activeIndex ?? "none"}
          items={LABELS}
          defaultActive={activeIndex}
          onItemClick={(index) => onChange(ORDER[index])}
          accentColor="hsl(var(--brand-blue))"
          textColor="hsl(var(--foreground))"
          markerColor="hsl(var(--border))"
          proximityRadius={120}
          fontSize={0.92}
          markerLength={18}
          markerGap={0}
          itemGap={28}
          maxShift={8}
          tickScale={0.26}
          falloff="linear"
        />
      </div>
    </div>
  );
}
